import { useMemo, useState } from 'react';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { safeParseConfig } from '../../exercise-templates/blockConfig';
import { renderRichText } from '../../../utils/richText';
import { resolveCorrectPositions, correctItemBySlot, shuffleNotSolved } from '../../exercise-templates/orderingConfig';

const ORDERING_DEFAULT = { prompt: '', items: [], correctPositions: [] };

function Chip({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
    const style = transform ? { transform: 'translate3d(' + transform.x + 'px,' + transform.y + 'px,0)' } : undefined;
    return (
        <span ref={setNodeRef} style={style} {...listeners} {...attributes}
            className={'course-preview-word-chip course-preview-ordering-chip' + (isDragging ? ' dragging' : '')}>
            {children}
        </span>
    );
}

function Slot({ id, number, children }) {
    const { setNodeRef, isOver } = useDroppable({ id });
    return (
        <div className='course-preview-ordering-slot-row'>
            <span className='course-preview-ordering-slot-number'>{number}</span>
            <div ref={setNodeRef} className={'course-preview-ordering-slot' + (isOver ? ' over' : '')}>{children}</div>
        </div>
    );
}

function Pool({ children }) {
    const { setNodeRef, isOver } = useDroppable({ id: 'pool' });
    return <div ref={setNodeRef} className={'course-preview-sentence-bank course-preview-ordering-pool' + (isOver ? ' over' : '')}>{children}</div>;
}

// Live "what will a student see" simulator: items start shuffled in a pool
// and are dragged into numbered slots (one per item). Nothing is graded or
// revealed here - like every sibling preview - the student's saved answer is
// reviewed with its correct/wrong coloring in the CRM workspace
// (AnsweredOrdering), and the real "check" happens on the student platform.
export default function OrderingDnd({ block }) {
    const cfg = safeParseConfig(block.config, ORDERING_DEFAULT);
    const items = useMemo(() => (cfg.items || []), [cfg.items]);
    const [pool, setPool] = useState(() => {
        const positions = resolveCorrectPositions(items, cfg.correctPositions);
        return shuffleNotSolved(items.map((_, i) => i), correctItemBySlot(positions));
    });
    const [slots, setSlots] = useState(() => items.map(() => null));
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    if (items.length === 0) return <span className='exercise-block-empty-hint'>Порожньо</span>;

    const handleDragEnd = ({ active, over }) => {
        if (!over) return;
        const itemIdx = Number(String(active.id).replace('item-', ''));
        const fromSlot = slots.indexOf(itemIdx);
        const nextSlots = [...slots];
        let nextPool = pool.filter((x) => x !== itemIdx);
        if (fromSlot !== -1) nextSlots[fromSlot] = null;

        if (over.id === 'pool') {
            nextPool = [...nextPool, itemIdx];
        } else {
            const target = Number(String(over.id).replace('slot-', ''));
            const displaced = nextSlots[target];
            nextSlots[target] = itemIdx;
            if (displaced != null) {
                if (fromSlot !== -1) nextSlots[fromSlot] = displaced;
                else nextPool = [...nextPool, displaced];
            }
        }
        setSlots(nextSlots);
        setPool(nextPool);
    };

    return (
        <div className='course-preview-question'>
            {cfg.prompt ? <p className='course-preview-question-text'>{renderRichText(cfg.prompt)}</p> : ''}
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <div className='course-preview-ordering-slots'>
                    {slots.map((itemIdx, s) => (
                        <Slot key={s} id={'slot-' + s} number={s + 1}>
                            {itemIdx != null ? <Chip id={'item-' + itemIdx}>{renderRichText(items[itemIdx])}</Chip> : <span className='course-preview-sentence-placeholder'>Перетягніть сюди</span>}
                        </Slot>
                    ))}
                </div>
                <Pool>
                    {pool.map((itemIdx) => <Chip key={itemIdx} id={'item-' + itemIdx}>{renderRichText(items[itemIdx])}</Chip>)}
                </Pool>
            </DndContext>
        </div>
    );
}
